import { ChangeEvent, useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions } from 'entities';

import { Button, Column, Loader, Avatar } from 'components/Blocks';
import { ImagesIcon } from 'components/Icons';

import Modal from './Modal';

import { useAuth } from 'providers/Auth';

import { cn } from 'styles';

const HEADERS_RECEIVED = 2;

function UserPicture(): JSX.Element {
  const {
    user: { data },
    updateUserMainData,
  } = useAuth();

  const { t } = useLang();

  const [fileConfig, setFileConfig] = useState<{
    file: File;
    readResult: string;
  } | null>(null);

  const handlePictureChange = useCallback(async ({ target }: ChangeEvent<HTMLInputElement>) => {
    const [file] = target.files || [];

    if (!file || !file.type.startsWith('image')) return;

    const reader = new FileReader();

    reader.onload = () => {
      if (reader.readyState === HEADERS_RECEIVED && reader.result) {
        setFileConfig({ file, readResult: reader.result as string });
      }
    };

    reader.readAsDataURL(file);
  }, []);

  const changePicture = actions.users.changeOwnPicture.use({
    onSuccess: ({ url }) => updateUserMainData({ picture: url }),
  });

  const updateUserImage = async (base64: string | null): Promise<void> => {
    if (!fileConfig) return;

    const { file } = fileConfig;

    setFileConfig(null);

    changePicture.execute({ file, userId: data.id, base64 });
  };

  return (
    <>
      <Modal
        onSelection={updateUserImage}
        fileConfig={fileConfig}
        close={() => setFileConfig(null)}
      />

      <Column alignItems="center">
        <div className="relative">
          <div className={cn(changePicture.loading && 'blur-sm')}>
            <Avatar
              src={data.picture || ''}
              alt={data.name}
              className="h-28 w-28"
              fallback="no-user-avatar"
            />
          </div>

          {changePicture.loading && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none">
              <Loader type="dualRing" size={32} />
            </div>
          )}
        </div>

        <Button className="relative" variant="ghost">
          <ImagesIcon />

          <span>{t('change-picture')}</span>

          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="picture-input" className="absolute inset-0 cursor-pointer">
            <input
              type="file"
              id="picture-input"
              name="picture-input"
              accept="image/*"
              className="hidden"
              onChange={handlePictureChange}
            />
          </label>
        </Button>
      </Column>
    </>
  );
}

export default UserPicture;
